/**
 * id-reference-registry.js — the single declarative table of every way an HTML
 * document refers to an element id. The export id-plan (A1), the DOM rewriter
 * (A2), the CSS rewriter (A3), the section-JS facade (A4), and preflight
 * validation all DERIVE from this table; never give a consumer its own copy
 * (the motion-patterns.js registry rule).
 *
 * Pure data + tiny pure helpers. No DOM, no imports.
 */

/** Attributes whose value is exactly one id. */
export const SINGLE_IDREF_ATTRS = [
  'for',
  'list',
  'form',
  'aria-activedescendant',
  'aria-details',
  'aria-errormessage',
  'popovertarget',
  'commandfor'
];

/** Attributes whose value is a space-separated list of ids. */
export const IDREF_LIST_ATTRS = [
  'aria-labelledby',
  'aria-describedby',
  'aria-controls',
  'aria-owns',
  'aria-flowto',
  'headers',
  'itemref'
];

/**
 * Attributes that may carry a `#fragment` URL reference to an id — as `#id`
 * alone or `path#id`. `href` covers <a>/<area>/SVG <use>; xlink:href is the
 * SVG legacy namespace form.
 */
export const FRAGMENT_URL_ATTRS = ['href', 'xlink:href'];

/**
 * Presentation attributes (and inline-style/CSS properties) whose value may
 * contain `url(#id)` references to SVG paint servers, filters, masks and
 * clip paths.
 */
export const URL_FUNCTION_ATTRS = ['fill', 'stroke', 'clip-path', 'mask', 'filter', 'style'];

/** CSS properties whose declaration values may contain `url(#id)`. */
export const URL_FUNCTION_CSS_PROPS = [
  'fill',
  'stroke',
  'clip-path',
  'mask',
  'mask-image',
  'filter',
  'background',
  'background-image',
  'offset-path'
];

/** `usemap="#id"` on <img>/<object> pairs with <map name="id">. */
export const USEMAP_ATTR = 'usemap';

/** Split an IDREF-list attribute value into tokens. */
export function splitIdrefList(value) {
  return String(value || '')
    .split(/\s+/)
    .filter(Boolean);
}

/** Parse `#id` / `path#id` — returns {path, id} or null when there is no hash. */
export function splitFragmentUrl(value) {
  const raw = String(value || '');
  const hashIndex = raw.indexOf('#');
  if (hashIndex === -1) return null;
  const id = raw.slice(hashIndex + 1);
  if (!id || id === '!') return null;
  return { path: raw.slice(0, hashIndex), id };
}

const URL_FUNCTION_RE = /url\(\s*(['"]?)#([^'")\s]+)\1\s*\)/g;

/** All `url(#id)` ids inside a CSS or attribute value. */
export function idsInUrlFunctions(value) {
  const ids = [];
  const text = String(value || '');
  let match;
  URL_FUNCTION_RE.lastIndex = 0;
  while ((match = URL_FUNCTION_RE.exec(text)) !== null) ids.push(match[2]);
  return ids;
}

/**
 * Collect every id an element subtree REFERENCES (not the ids it declares).
 * `root` is any DOM element; works under browser DOM and jsdom alike.
 */
export function collectReferencedIds(root) {
  const referenced = new Set();
  if (!root || typeof root.querySelectorAll !== 'function') return referenced;
  const all = [root, ...root.querySelectorAll('*')];
  for (const el of all) {
    if (typeof el.getAttribute !== 'function') continue;
    for (const attr of SINGLE_IDREF_ATTRS) {
      const v = el.getAttribute(attr);
      if (v) referenced.add(v.trim());
    }
    for (const attr of IDREF_LIST_ATTRS) {
      for (const token of splitIdrefList(el.getAttribute(attr))) referenced.add(token);
    }
    for (const attr of FRAGMENT_URL_ATTRS) {
      const parsed = splitFragmentUrl(el.getAttribute(attr));
      if (parsed) referenced.add(parsed.id);
    }
    const usemap = el.getAttribute(USEMAP_ATTR);
    if (usemap && usemap.startsWith('#')) referenced.add(usemap.slice(1));
    for (const attr of URL_FUNCTION_ATTRS) {
      for (const id of idsInUrlFunctions(el.getAttribute(attr))) referenced.add(id);
    }
  }
  return referenced;
}
